import { RelayPageDataTable } from "@constellation-academy/epic-ui";

import { useState } from "react";
import { useLazyLoadQuery, useMutation } from "react-relay";
import { useTranslation } from "@i18n/hooks";
import { type featureTogglesScreen_EditFeatureToggleMutation } from "@relay/featureTogglesScreen_EditFeatureToggleMutation.graphql";
import {
	type featureTogglesScreen_FeatureToggleFragment$data,
	type featureTogglesScreen_FeatureToggleFragment$key,
} from "@relay/featureTogglesScreen_FeatureToggleFragment.graphql";
import { type featureTogglesScreen_Query } from "@relay/featureTogglesScreen_Query.graphql";
import { type featureTogglesScreen_QueryFragment$key } from "@relay/featureTogglesScreen_QueryFragment.graphql";
import { type featureTogglesScreen_Refetch } from "@relay/featureTogglesScreen_Refetch.graphql";

import {
	EDIT_FEATURE_TOGGLE_MUTATION,
	FEATURE_TOGGLE_FRAGMENT,
	QUERY,
	QUERY_FRAGMENT,
} from "./feature-toggles.graphql";
import { FeatureToggleActivationCell } from "./parts/feature-toggle-activation-cell";
import { BaseScreen } from "../../components/base-screen";
import { NoAccess } from "../../components/no-access";
import { useHasPermissions } from "../../hooks/use-has-permissions";

export const FeatureTogglesScreen = () => {
	const { t } = useTranslation("common");
	const query = useLazyLoadQuery<featureTogglesScreen_Query>(QUERY, {});

	const isRoot = useHasPermissions("onlyOwnerOfRoot");

	const [editingId, setEditingId] = useState<string>();

	const [editFeatureToggle, isEditingFeatureToggle] =
		useMutation<featureTogglesScreen_EditFeatureToggleMutation>(EDIT_FEATURE_TOGGLE_MUTATION);

	if (!isRoot) return <NoAccess />;

	const createActiveOnChangeHandler =
		(feature: featureTogglesScreen_FeatureToggleFragment$data) => (checked: boolean) => {
			setEditingId(feature.id);

			editFeatureToggle({
				variables: {
					input: {
						id: feature.id,
						isActive: checked,
						name: feature.data.name,
					},
				},
				onCompleted: () => {
					setEditingId(undefined);
				},
				onError: () => {
					setEditingId(undefined);
				},
			});
		};

	return (
		<BaseScreen>
			<h1>{t("featureToggles.screenTitle")}</h1>

			<div className="w-full mt-4">
				<RelayPageDataTable<
					featureTogglesScreen_Refetch,
					featureTogglesScreen_QueryFragment$key,
					featureTogglesScreen_FeatureToggleFragment$key
				>
					queryFragment={QUERY_FRAGMENT}
					parentFragmentRef={query}
					nodeInlineFragment={FEATURE_TOGGLE_FRAGMENT}
					extractConnection={(data) => data?.Admin?.FeatureToggle?.featureToggles}
					emptyMessage={t("featureToggles.noFeaturesAvailable")}
				>
					{(Column) => (
						<>
							<Column header={t("featureToggles.feature")} field="data.name" />
							<Column
								header={t("table.activated")}
								field="data.isActive"
								body={(feature) => {
									return (
										<FeatureToggleActivationCell
											featureToggle={feature}
											isEditing={
												editingId === feature.id && isEditingFeatureToggle
											}
											onActiveChange={createActiveOnChangeHandler(feature)}
										/>
									);
								}}
							/>
						</>
					)}
				</RelayPageDataTable>
			</div>
		</BaseScreen>
	);
};
